import { Button, Modal, useToast } from "@design";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiError, schemas } from "@/api";
import { useAppForm } from "@/components/form/form";
import { defaultPagePath, NODE_TYPES, type NodeType, newPageContent } from "./nodeTypes";
import { useSaveWikiFile, useWikiErrorMessage } from "./useWiki";

// Path length rules come from the generated WikiFileSave schema; the backend
// owns the rest (must end in .md, no reserved names) and explains rejections.
const save = schemas.zWikiFileSave.shape;

function newPageSchema(titleRequired: string) {
  return z.object({
    title: z.string().trim().min(1, titleRequired),
    type: z.enum(NODE_TYPES),
    path: save.path.or(z.literal("")),
  });
}

/**
 * Create a page: title + type, and an optional path (defaults to
 * `<type folder>/<slug>.md`). Mount only while open so it starts fresh.
 */
export function NewPageModal({
  spaceUuid,
  onClose,
  onCreated,
}: Readonly<{ spaceUuid: string; onClose: () => void; onCreated: (path: string) => void }>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useWikiErrorMessage();
  const create = useSaveWikiFile(spaceUuid);

  const form = useAppForm({
    defaultValues: { title: "", type: "person" as NodeType, path: "" },
    validators: { onChange: newPageSchema(t("create.titleRequired")) },
    onSubmit: async ({ value }) => {
      const path = value.path.trim() || defaultPagePath(value.type, value.title);
      try {
        const res = await create.mutateAsync({
          path: { space_uuid: spaceUuid },
          body: { path, content: newPageContent(value.type, value.title), baseRevision: null },
          throwOnError: true,
        });
        toast.success(t("create.created"));
        onCreated(res.data.path);
      } catch (error) {
        const exists = error instanceof ApiError && error.code === "WIKI_CONFLICT";
        toast.error(exists ? t("create.exists") : message(error, t("editor.saveFailed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t("create.title")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("editor.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {t("create.submit")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={t("create.title")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="title">{(field) => <field.TextField label={t("create.name")} required />}</form.AppField>
        <form.AppField name="type">
          {(field) => (
            <field.SelectField
              label={t("create.type")}
              options={NODE_TYPES.map((type) => ({ value: type, label: t(`type.${type}`) }))}
            />
          )}
        </form.AppField>
        <form.Subscribe selector={(s) => defaultPagePath(s.values.type, s.values.title)}>
          {(suggested) => (
            <form.AppField name="path">
              {(field) => <field.TextField label={t("create.path")} placeholder={suggested || "folder/page.md"} />}
            </form.AppField>
          )}
        </form.Subscribe>
      </form>
    </Modal>
  );
}
